import {
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  signal,
  Signal,
  untracked
} from '@angular/core';
import { Router } from '@angular/router';
import { TaskFiltersService } from '@app/components/task/task-list-toolbar/task-filters.service';
import { TaskListToolbarComponent } from '@app/components/task/task-list-toolbar/task-list-toolbar.component';
import { TaskSplitviewListComponent } from '@app/components/task/task-splitview-list/task-splitview-list.component';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { Task } from '@app/shared/types/models/task/task.model';
import { TasksPageData } from '@app/shared/types/models/tasks-page-data';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import { ActiveTaskService } from '@app/views/company/projects/tasks/active-task.service';
import { TasksListActionsService } from '@app/views/company/projects/tasks/tasks-list-actions.service';

@Component({
  selector: 'proffeo-project-tasks-view',
  templateUrl: './project-tasks-view.component.html',
  imports: [UiPageWrapperComponent, TaskSplitviewListComponent, TaskListToolbarComponent],
  providers: []
})
export class ProjectTasksViewComponent implements OnInit {
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly router: Router = inject(Router);
  private readonly tasksListService: TasksListActionsService = inject(TasksListActionsService);
  private readonly activeTaskService: ActiveTaskService = inject(ActiveTaskService);
  private readonly taskFiltersState: TaskFiltersService = inject(TaskFiltersService);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly hasLoadedTaskList = signal(false);

  protected readonly taskListViewMode = this.taskFiltersState.viewMode;
  protected total = signal(0);
  protected isMyTasksMode = false;
  protected selectedTabIndex = 0;
  protected tasks: Signal<TasksPageData | undefined> = this.tasksListService.filteredTasksPage;
  protected activeTask: Signal<Task> = this.activeTaskService.activeTask;

  protected isLoading = computed(() => {
    const currentTasks = this.tasks();
    return !this.hasLoadedTaskList() && (!currentTasks || currentTasks.content === undefined);
  });

  public constructor() {
    this.loaderService.suppressOverlay();
    this.destroyRef.onDestroy(() => this.loaderService.resumeOverlay());

    effect(() => {
      const tasks = this.tasks();
      if (tasks?.content !== undefined) {
        this.hasLoadedTaskList.set(true);
      }
    });

    effect(() => {
      const tasks = this.tasks();
      if (!tasks) return;

      untracked(() => {
        this.taskFiltersState.setFiltersFromResponse(tasks.filtersId, tasks.filters);
      });
    });

    effect(() => {
      const task = this.activeTask();
      if (!task) return;

      if (task.name) return;

      const allTasks = this.tasks()?.content || [];
      const taskSignal = allTasks.find(t => t().id === task.id);
      const found = taskSignal?.();

      if (found) {
        void this.activeTaskService.setActiveTask(found);
        return;
      }

      const taskById: Task = this.tasksListService.getTaskById(task.id)();
      if (taskById) {
        void this.activeTaskService.setActiveTask(taskById);
      }
    });
  }

  protected get isMobile(): Signal<boolean> {
    return this.deviceService.isMobile;
  }

  @HostListener('window:keydown.escape')
  protected onDetailsClosed(): void {
    if (!this.isMobile() && this.activeTask()) {
      void this.activeTaskService.clearActiveTask();
    }
  }

  public ngOnInit(): void {
    this.isMyTasksMode = this.router.url.includes('/my-tasks');
    this.selectedTabIndex = this.isMyTasksMode ? 1 : 0;
  }
}
